import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Banknote, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusPill = (status) => {
  const map = {
    requested: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
    approved: 'border-blue-400/20 bg-blue-500/10 text-blue-200',
    paid: 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
    on_hold: 'border-rose-400/20 bg-rose-500/10 text-rose-200',
    rejected: 'border-slate-400/20 bg-slate-500/10 text-slate-200',
    connected: 'border border-primary/20 bg-primary/10 text-primary',
    pending_credentials: 'border border-amber-400/20 bg-amber-500/10 text-amber-200',
  };
  return map[(status || '').toLowerCase()] || 'border-border-subtle bg-card/40 text-text-muted';
};

const MetricCard = ({ icon: Icon, label, value, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-1 text-2xl font-bold text-text-heading">{value}</p>
      </div>
    </div>
  </div>
);

const buildPartnerOptionLabel = (item) => `${String(item?.name || '')} · ${String(item?.plan_label || '')}`;
const buildPackageOptionLabel = (item) => String(item?.label || '');

export const PartnerOfficeAdminHub = ({ authFetch }) => {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [commissionForm, setCommissionForm] = useState({
    partner_user_id: '',
    kind: 'manual_adjustment',
    description: '',
    amount_eur: '',
    maintenance_package_id: '',
    notes: '',
  });

  const loadHub = useCallback(async (showToast = false) => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/admin/partner-office`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar Partner Office');
      setHub(data);
      if (showToast) toast.success('Partner Office actualizado');
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar Partner Office');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadHub(false);
  }, [loadHub]);

  const filteredPartners = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (hub?.partner_cards || []).filter((item) => !term || [item.name, item.email, item.role, item.plan_label, item.country].filter(Boolean).join(' ').toLowerCase().includes(term));
  }, [hub, search]);

  const updatePayout = async (requestId, status) => {
    try {
      setSaving(true);
      const res = await authFetch(`${API}/admin/partner-office/payouts/${requestId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar el retiro');
      toast.success(`Solicitud marcada como ${status}`);
      await loadHub(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar el retiro');
    } finally {
      setSaving(false);
    }
  };

  const submitCommission = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await authFetch(`${API}/admin/partner-office/commissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...commissionForm,
          amount_eur: Number(commissionForm.amount_eur || 0),
          maintenance_package_id: commissionForm.maintenance_package_id || null,
          notes: commissionForm.notes || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear la comisión');
      toast.success('Comisión registrada correctamente');
      setCommissionForm({ partner_user_id: '', kind: 'manual_adjustment', description: '', amount_eur: '', maintenance_package_id: '', notes: '' });
      await loadHub(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo crear la comisión');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !hub) {
    return <div className="glass-card p-8 text-center text-text-muted" data-testid="partner-office-admin-loading">Cargando Partner Office…</div>;
  }

  if (!hub) return null;

  return (
    <div className="space-y-6" data-testid="partner-office-admin-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="partner-office-admin-line-identity-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Neuralia Partner</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · cockpit modular de red comercial</p>
            <h3 className="text-3xl font-semibold text-text-heading">Operación central de la línea Partner</h3>
            <p className="mt-4 max-w-4xl text-base text-text-body">Desde aquí administras red comercial, comisiones, payouts e integraciones de la línea Partner. El modelo recomendado no limita clientes: limita uso operativo de agentes, créditos y capacidad automatizada para proteger márgenes y calidad.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="partner-office-admin-diagnosis-chips">
              <span className="insight-chip">Cockpit ejecutivo</span>
              <span className="insight-chip">Balances visibles</span>
              <span className="insight-chip">Bloqueos claros</span>
              <span className="insight-chip">Acción rápida</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard icon={Users} label="Partners activos" value={hub.metrics?.partner_profiles || 0} testId="partner-office-admin-metric-profiles" />
            <MetricCard icon={ArrowRightLeft} label="Retiros pendientes" value={hub.metrics?.payout_requests_pending || 0} testId="partner-office-admin-metric-payouts" />
            <MetricCard icon={Banknote} label="Comisiones" value={hub.metrics?.commission_entries || 0} testId="partner-office-admin-metric-commissions" />
            <MetricCard icon={ShieldCheck} label="Canales listos" value={hub.metrics?.connected_socials || 0} testId="partner-office-admin-metric-socials" />
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3" data-testid="partner-office-admin-priority-grid">
        <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="partner-office-admin-whatsapp-readiness-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">WhatsApp Cloud API</p>
          <h4 className="mt-2 text-lg font-semibold text-text-heading">Base de integración lista</h4>
          <p className="mt-3 text-sm text-text-body">Estado actual: <span className="text-text-heading">{hub.whatsapp_readiness?.status}</span></p>
          <p className="mt-2 text-sm text-text-body">{hub.whatsapp_readiness?.next_step}</p>
          {!!(hub.whatsapp_readiness?.missing_credentials || []).length && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="partner-office-admin-whatsapp-missing-credentials">
              {(hub.whatsapp_readiness?.missing_credentials || []).map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}
            </div>
          )}
        </div>

        <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="partner-office-admin-controls-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Controles admin</p>
              <h4 className="mt-2 text-lg font-semibold text-text-heading">Qué controla este cockpit</h4>
          <div className="mt-4 space-y-2 text-sm text-text-body">
            {(hub.admin_controls || []).map((item) => <div key={item}>• {item}</div>)}
                <div>• Los planes Partner no deben limitar número de clientes; limitan intensidad de uso de agentes, créditos y automatización.</div>
          </div>
        </div>

        <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="partner-office-admin-future-work-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Narrativa comercial</p>
          <h4 className="mt-2 text-lg font-semibold text-text-heading">Future of work signals</h4>
          <div className="mt-4 space-y-2 text-sm text-text-body">
            {(hub.future_of_work || []).slice(0, 4).map((item) => <div key={item}>• {item}</div>)}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="glass-card p-6" data-testid="partner-office-admin-integrations-readiness-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Stack listo para activar</p>
              <h3 className="text-xl font-semibold text-text-heading">Estado de integraciones clave</h3>
            </div>
            <button type="button" className="btn-secondary" onClick={() => loadHub(true)} data-testid="partner-office-admin-refresh-button">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refrescar
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {(hub.integrations_readiness || []).map((item) => (
              <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-office-admin-integration-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                    <p className="mt-1 text-xs text-text-muted">{item.helper}</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.status)}`}>{item.status}</span>
                </div>
                {!!item.missing?.length && <div className="mt-3 flex flex-wrap gap-2">{item.missing.map((value) => <span key={value} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{value}</span>)}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="partner-office-admin-whatsapp-steps-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Guía operativa</p>
          <h3 className="text-xl font-semibold text-text-heading">Activación paso a paso de WhatsApp</h3>
          <div className="mt-4 space-y-3 text-sm text-text-body">
            {(hub.whatsapp_activation_steps || []).map((item, index) => (
              <div key={index} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`partner-office-admin-whatsapp-step-${index + 1}`}>{item}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <form onSubmit={submitCommission} className="glass-card p-6 space-y-3" data-testid="partner-office-admin-commission-form">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Admin control</p>
          <h3 className="text-xl font-semibold text-text-heading">Registrar comisión manual</h3>
          <select className="input-kinesthetic" value={commissionForm.partner_user_id} onChange={(e) => setCommissionForm((prev) => ({ ...prev, partner_user_id: e.target.value }))} data-testid="partner-office-admin-commission-partner-select">
            <option value="">Selecciona partner</option>
            {(hub.partner_cards || []).map((item) => <option key={item.partner_user_id} value={item.partner_user_id}>{buildPartnerOptionLabel(item)}</option>)}
          </select>
          <div className="grid gap-3 md:grid-cols-2">
            <input className="input-kinesthetic" placeholder="Tipo" value={commissionForm.kind} onChange={(e) => setCommissionForm((prev) => ({ ...prev, kind: e.target.value }))} data-testid="partner-office-admin-commission-kind-input" />
            <input className="input-kinesthetic" type="number" min="0" placeholder="Importe (€)" value={commissionForm.amount_eur} onChange={(e) => setCommissionForm((prev) => ({ ...prev, amount_eur: e.target.value }))} data-testid="partner-office-admin-commission-amount-input" />
          </div>
          <input className="input-kinesthetic" placeholder="Descripción" value={commissionForm.description} onChange={(e) => setCommissionForm((prev) => ({ ...prev, description: e.target.value }))} data-testid="partner-office-admin-commission-description-input" />
          <select className="input-kinesthetic" value={commissionForm.maintenance_package_id} onChange={(e) => setCommissionForm((prev) => ({ ...prev, maintenance_package_id: e.target.value }))} data-testid="partner-office-admin-commission-package-select">
            <option value="">Sin paquete</option>
            {(hub.maintenance_packages || []).map((item) => <option key={item.id} value={item.id}>{buildPackageOptionLabel(item)}</option>)}
          </select>
          <textarea className="input-kinesthetic resize-none" rows={4} placeholder="Notas internas" value={commissionForm.notes} onChange={(e) => setCommissionForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="partner-office-admin-commission-notes-input" />
          <button type="submit" className="btn-primary" disabled={saving} data-testid="partner-office-admin-commission-submit-button">{saving ? 'Guardando...' : 'Crear comisión'}</button>
        </form>

        <div className="glass-card p-6" data-testid="partner-office-admin-partners-list">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Vista ejecutiva</p>
              <h3 className="text-xl font-semibold text-text-heading">Partners y balances</h3>
            </div>
            <input className="input-kinesthetic max-w-[260px]" placeholder="Buscar partner o plan" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="partner-office-admin-search-input" />
          </div>
          <div className="mt-4 space-y-3">
            {filteredPartners.map((item) => (
              <div key={item.partner_user_id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-office-admin-card-${item.partner_user_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                    <p className="mt-1 text-xs text-text-muted">{item.email} · {item.role}</p>
                  </div>
                  <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item.plan_label}</span>
                </div>
                <div className="mt-4 grid gap-3 text-sm md:grid-cols-4">
                  <div>
                    <p className="text-text-muted">Disponible</p>
                    <p className="mt-1 font-semibold text-text-heading">€{Number(item.available_balance_eur || 0).toLocaleString('es-ES')}</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Reservado</p>
                    <p className="mt-1 font-semibold text-text-heading">€{Number(item.requested_payouts_eur || 0).toLocaleString('es-ES')}</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Social</p>
                    <p className="mt-1 font-semibold text-text-heading">{item.connected_socials} canales</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Modo</p>
                    <p className="mt-1 font-semibold text-text-heading">{item.autopilot_mode}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="partner-office-admin-payouts-list">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Retiros</p>
        <h3 className="text-xl font-semibold text-text-heading">Aprobación y liquidación</h3>
        <div className="mt-4 space-y-3">
          {(hub.payout_requests || []).map((item) => (
            <div key={item.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-office-admin-payout-${item.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-text-heading">{item.payout_method}</p>
                  <p className="mt-1 text-xs text-text-muted">{item.partner_user_id}</p>
                </div>
                <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.status)}`}>{item.status}</span>
              </div>
              <p className="mt-3 text-lg font-bold text-primary">€{Number(item.amount_eur || 0).toLocaleString('es-ES')}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['approved', 'on_hold', 'paid', 'rejected'].map((status) => (
                  <button key={status} type="button" className="btn-secondary text-xs" onClick={() => updatePayout(item.id, status)} disabled={saving} data-testid={`partner-office-admin-payout-${item.id}-${status}-button`}>
                    {status}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PartnerOfficeAdminHub;